import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import DataTable from "../DataTable";
import { createTableColumns } from "./tableConfig";
import { selectUser } from "../../store/selectors/authSelectors";
import {
  selectDashboardPatients,
  selectDashboardLoading,
  selectDashboardError,
} from "../../store/selectors/dashboardSelectors";
import {
  ErrorContainer,
  ErrorText,
  RetryButton,
  EmptyStateContainer,
  EmptyStateImage,
  EmptyStateText,
} from "./DashboardPatientsList.styled";
import { ASSISTANT_ROLES } from "../../constants";
import webPageImage from "../../assets/images/web-page.jpg";
import folderFileImage from "../../assets/images/folder-file.jpg";
import { Patient } from "../../services/patientsService";

interface PatientsTableProps {
  activeTab: 1 | 2 | 3;
  searchTerm: string;
  sortBy: string;
  sortOrder: "asc" | "desc";
  currentPage: number;
  pageSize: number;
  totalCount: number;
  onSort: (field: string, direction: "asc" | "desc") => void;
  onPageChange: (page: number) => void;
  onPatientClick: (patientId: number) => void;
  onProviderClick: (providerId: number) => void;
  onTreatmentPlanClick: (treatmentPlanId: number) => void;
  onRetry: () => void;
}

const PatientsTable: React.FC<PatientsTableProps> = ({
  activeTab,
  searchTerm,
  sortBy,
  sortOrder,
  currentPage,
  pageSize,
  totalCount,
  onSort,
  onPageChange,
  onPatientClick,
  onProviderClick,
  onTreatmentPlanClick,
  onRetry,
}) => {
  const user = useSelector(selectUser);
  const allPatients = useSelector(selectDashboardPatients);
  const loading = useSelector(selectDashboardLoading);
  const error = useSelector(selectDashboardError);

  // Filter out patients without treatment plans - they shouldn't appear in dashboard tabs
  const patients = useMemo(
    () => allPatients.filter((patient) => patient.latestTreatmentPlanId),
    [allPatients],
  );

  // Show provider column for Manager/Assistant roles
  const showProviderColumn = ASSISTANT_ROLES.includes(
    user?.roleTitle as (typeof ASSISTANT_ROLES)[number],
  );

  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  }, []);

  const getEmptyStateConfig = useCallback(() => {
    switch (activeTab) {
      case 1:
        return {
          image: webPageImage,
          message: "No plans awaiting review yet",
        };
      case 2:
        return {
          image: folderFileImage,
          message: "No plans in this list yet",
        };
      case 3:
        return {
          image: folderFileImage,
          message: "No plans in this list yet",
        };
      default:
        return {
          image: folderFileImage,
          message: "No data found.",
        };
    }
  }, [activeTab]);

  const emptyStateConfig = getEmptyStateConfig();

  const emptyContent = useMemo(
    () => (
      <EmptyStateContainer>
        <EmptyStateImage src={emptyStateConfig.image} alt="No data" />
        <EmptyStateText>{emptyStateConfig.message}</EmptyStateText>
      </EmptyStateContainer>
    ),
    [emptyStateConfig],
  );

  const columns = useMemo(
    () =>
      createTableColumns({
        activeTab,
        showProviderColumn,
        onPatientClick,
        onProviderClick,
        onTreatmentPlanClick,
        formatDate,
      }),
    [
      activeTab,
      showProviderColumn,
      onPatientClick,
      onProviderClick,
      onTreatmentPlanClick,
      formatDate,
    ],
  );

  if (error) {
    return (
      <ErrorContainer>
        <ErrorText>{error}</ErrorText>
        <RetryButton variant="outlined" onClick={onRetry}>
          Retry
        </RetryButton>
      </ErrorContainer>
    );
  }

  // Use filtered patients count for display
  const filteredTotalCount = patients.length < allPatients.length 
    ? patients.length 
    : totalCount;

  return (
    <DataTable<Patient>
      columns={columns}
      data={patients}
      loading={loading}
      sortField={sortBy}
      sortDirection={sortOrder}
      onSort={onSort}
      currentPage={currentPage}
      onPageChange={onPageChange}
      pageSize={pageSize}
      totalCount={filteredTotalCount}
      searchQuery={searchTerm}
      onSearch={() => {}}
      emptyContent={emptyContent}
    />
  );
};

export default React.memo(PatientsTable);
